import React, { useCallback, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import { Buttons, CancelButton, Drawer, Form } from '~/components';
import ReadForm from '~/components/read-only/ReadForm';
import ReadTextbox from '~/components/read-only/ReadTextbox';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDocumentTitle } from '~/hooks';
import { ErrorPage } from '~/routes/public/pages';
import { colors } from '~/styles';

const MarkdownContent = styled.div`
  font-size: 0.875rem;
  line-height: 1.6;
  color: ${colors.black};

  h1,
  h2,
  h3 {
    margin: 0.75rem 0 0.25rem;
    &:first-child {
      margin-top: 0;
    }
  }

  p {
    margin: 0.25rem 0;
  }

  ul,
  ol {
    margin: 0.25rem 0;
    padding-left: 1.5rem;
  }

  code {
    background: ${colors.grey5};
    padding: 0.125rem 0.25rem;
    border-radius: 0.1875rem;
    font-size: 0.8125rem;
  }

  pre {
    background: ${colors.grey5};
    padding: 0.5rem;
    border-radius: 0.25rem;
    overflow-x: auto;
  }

  blockquote {
    margin: 0.25rem 0;
    padding-left: 0.75rem;
    border-left: 3px solid ${colors.grey10};
    color: ${colors.grey55};
  }
`;

const allowedElements = ['p', 'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'strong', 'em', 'pre', 'code', 'blockquote', 'br'];

const scopeLabels = {
  system: 'Standard',
  workspace: 'Workspace',
  member: 'Personal',
};

export default function AssistantProfileView({ profileId, onClose }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();

  const [query, setQuery] = useState({ isReady: false, data: null });
  const fetchData = useCallback(async () => {
    if (!profileId) {
      setQuery({ isReady: true, data: {} });
      return;
    }

    try {
      const { data } = await api.www.workspaces(workspace.id).ai().assistantProfiles(profileId).get();
      setQuery({ isReady: true, data });
    } catch (error) {
      toast.error(error.message || 'There was a problem reading this profile. Please try again.');
    }
  }, [workspace.id, profileId, api, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const profile = query?.data;

  const title = 'View Assistant Profile';
  useDocumentTitle(title);

  if (!query.isReady) return null;
  if (!query.data) return <ErrorPage.NotFound publicSite={false} />;

  return (
    <Drawer isOpen title={title} onClose={onClose}>
      <ReadForm>
        <ReadForm.Section title="Assistant Profile">
          <Form.Control>
            <ReadTextbox label="Name" value={profile.name} />
          </Form.Control>

          {profile.description && (
            <Form.Control>
              <ReadTextbox label="Description" value={profile.description} />
            </Form.Control>
          )}

          <Form.Control>
            <ReadTextbox label="Provider" value={profile.provider} />
          </Form.Control>

          <Form.Control>
            <ReadTextbox label="Model" value={profile.model} />
          </Form.Control>

          <Form.Control>
            <ReadTextbox label="Scope" value={scopeLabels[profile.scopeKey] || profile.scopeKey} />
          </Form.Control>

          <Form.Control>
            <MarkdownContent>
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkBreaks]}
                allowedElements={allowedElements}
                unwrapDisallowed>
                {profile.instructions}
              </ReactMarkdown>
            </MarkdownContent>
          </Form.Control>
        </ReadForm.Section>

        <Drawer.Actions>
          <Buttons align="right">
            <CancelButton onClick={onClose}>Close</CancelButton>
          </Buttons>
        </Drawer.Actions>
      </ReadForm>
    </Drawer>
  );
}
